import Vue from 'vue'
import store from '@/store/store'
import {getProfile} from '@/http/requests/profile'
import { loading } from '@/prototypes/u/loading'
import { checkPermissions, getAssetsUrl } from '@/prototypes/u/other'
import { cookie } from '@/prototypes/u/cookie'
import {getCompanyInfo} from '../http/requests/auth'
import {setLocale} from '@/prototypes/u/locale'

function validateUser (routeInfo) {
  return getProfile(routeInfo)
}


const $t = sign => Vue.prototype.i18n.t(sign)

function getCompanyInformation (routeName = {}) {
  getCompanyInfo(routeName).then((response) => {
    const company = response.data
    const clubInfo = {
      club_name: company.company_name,
      club_logo: company.company_avatar ? getAssetsUrl(company.company_avatar, '', routeName) : '',
      club_currency: company.club_currency || '',
      club_currency_code: company.club_currency_code || 'NUN',
      club_language: company.club_language || 'en',
      club_language_rtl: company.club_language_rtl || false,
      club_language_path: company.club_language_path || '',
      club_language_regex_script: company.club_language_regex_script || 'Latin',
      club_locale: company.club_locale || 'UAE',
      club_default_country_code: company.club_default_country_code || '',
      available_languages: company.available_languages,
      active_at: company.active_at,
      expired_at: company.expired_at,
      is_active: company.is_active
    }
    if (!routeName.params || !routeName.params.hasOwnProperty('tenant')) {
      clubInfo.club_core_name = company.company_name
      clubInfo.club_core_logo = company.company_avatar ? getAssetsUrl(company.company_avatar, '', routeName) : ''
    }
    store.dispatch('setting/setClubInfo', clubInfo)
    setLocale(company.club_language)
  })
}

function checkPageChange (next) {
  if (store.state.pageHaveChanges) {
    const answer =
      window.confirm($t('others.alert.unsavedChanges'))

    if (answer) {
      loading.show(true)
      store.dispatch('removePageChanges')
      next()
    } else {
      loading.hide()
      next(false)
    }
  } else {
    loading.show(true)
    next()
  }
}

export async function beforeEach (to, from, next) {
  /* in this place check user was logged in or not */

  try {
    store.dispatch('helper/changePrevRoute', from)
    store.dispatch('updateContentNavbarClass', '')
    store.dispatch('setPageTitle', '')
    const originDomain = window.location.href

    const originAccessToken = cookie.get(`accessToken.${originDomain}`)
    if (originAccessToken) {
      let domain = window.location.hostname.split('.').reverse()
      if (domain.length > 2) domain = domain.splice(0, 2)
      domain = domain.reverse().join('.')
      localStorage.setItem('accessToken', originAccessToken)
      cookie.delete(`accessToken.${originDomain}`, `${domain}`, '/')
    }

    const accessToken = localStorage.getItem('accessToken') || cookie.get('accessToken')

    if (accessToken) {
      await store.dispatch('auth/setAccessToken')

      if (from.name === null || (to.params.hasOwnProperty('tenant') && to.params.tenant !== from.params.tenant)) {
        getCompanyInformation(to)
      }
      const loadingEl = window.document.getElementById('loadingImg')
      if (loadingEl) {
        loadingEl.src = store.state.setting.clubInfo[to.params.hasOwnProperty('tenant') ? 'club_logo' : 'club_core_logo'] || '/logo.png'
      }
      if (to.name === 'page-login') {
        // the redirected navigation runs this guard again, so stop here to call next only once
        return next({name: 'selectCompany'})
      }

      let activeSessionsExceeded = false
      if (from.name === null || from.name === 'page-login') {
        // awaited so a failed profile request reaches the catch below instead of leaving the navigation pending
        const validate = await validateUser(to)
        if (validate.status !== 200) {
          throw 'auth-error'
        }

        const userData = validate.data.data

        const browserSetting = validate.data.data && validate.data.data.browser_setting ? validate.data.data.browser_setting : JSON.stringify({})
        const tableSettings = JSON.parse(browserSetting)
        // delete tableSettings.tables.payload
        if (tableSettings) {
          store.dispatch('table/setUserBrowserSettings', tableSettings)
        }

        const availableLanguages = store.state.setting.clubInfo.available_languages
        userData.route = to
        userData.language = availableLanguages.includes(tableSettings.language) ? tableSettings.language : store.state.setting.clubInfo.club_language
        userData.language_rtl = tableSettings.hasOwnProperty('language_rtl') && availableLanguages.includes(tableSettings.language) ? tableSettings.language_rtl : store.state.setting.clubInfo.club_language_rtl
        userData.language_path = tableSettings.hasOwnProperty('language_path') && availableLanguages.includes(tableSettings.language) ? tableSettings.language_path : store.state.setting.clubInfo.club_language_path
        store.dispatch('auth/updateUserInfo', userData)
        if (tableSettings.language && tableSettings.language !== store.state.setting.clubInfo.club_language && availableLanguages.includes(tableSettings.language)) {
          setLocale(tableSettings.language)
        }

        if (tableSettings && tableSettings.hasOwnProperty('sidebarCollapsed')) {
          store.commit('TOGGLE_REDUCE_BUTTON', tableSettings.sidebarCollapsed)
        }

        const userInfo = store.state.auth.user
        if (userInfo.role) {
          const allowedActiveSessions = userInfo.role.allowed_active_sessions ? userInfo.role.allowed_active_sessions : 3
          activeSessionsExceeded = to.name !== 'selectCompany' && userInfo.active_sessions > allowedActiveSessions
        }
      } else {
        const userInfo = store.state.auth.user
        if (userInfo.role) {
          activeSessionsExceeded = userInfo.active_sessions > userInfo.role.allowed_active_sessions
        }
      }

      if (activeSessionsExceeded && to.name !== 'Profile' && to.name !== 'profileActiveSessions') {
        loading.hide()
        return next({name: store.getters.isMobile ? 'profileActiveSessions' : 'Profile', params: {tenant: to.params.tenant}})
      }

      if (to.meta.hasOwnProperty('permission') && !checkPermissions(to.meta.permission)) {
        return next({name: 'page-error-404'})
      }
      checkPageChange(next)
    } else if (!to.matched.some(record => record.name === 'page-login')) {
      throw 'auth-error'
    } else {
      if (from.name === null || (to.params.hasOwnProperty('tenant') && to.params.tenant !== from.params.tenant)) {
        getCompanyInformation()
      }
      next()
    }
  } catch (e) {
    console.error(e)
    if (localStorage.getItem('accessToken') || cookie.get('accessToken')) {
      // the user is logged in but preparing the page failed (e.g. the profile request or a bug);
      // the login page would redirect back here and loop, so cancel this navigation instead
      loading.hide()
      next(false)
    } else if (!to.matched.some(record => record.name === 'page-login')) {
      next({
        name: 'page-login'
        /* query: {redirectFrom: to.fullPath}*/
      })
      // next(false)
    } else {
      next()
    }
  }
}
